/**
 * Xero Manual Form Component
 *
 * Manual entry form for creating/updating Xero invoice state.
 */

import { useTranslation } from 'react-i18next'
import { Save, Loader2, AlertCircle } from 'lucide-react'
import type { ManualXeroInvoiceRequest, XeroInvoiceStatus, ImportResponse } from '../../types'
import { XeroFormStatusAmounts } from './XeroFormStatusAmounts'
import { XeroFormDatesLinking } from './XeroFormDatesLinking'
import { XeroOrgFieldset } from './XeroOrgFieldset'
import { XeroResultAlert } from './XeroResultAlert'
import { XeroInvoiceReferenceFieldset } from './XeroInvoiceReferenceFieldset'
import { useXeroManualForm } from './use-xero-manual-form'
import type { XeroFormDataShape } from './use-xero-manual-form'

interface XeroManualFormProps {
  initialData?: Partial<ManualXeroInvoiceRequest>
  onSuccess?: (result: ImportResponse) => void
  onError?: (error: Error) => void
  onCancel?: () => void
  onCreateOrganisation?: (xeroOrganisationId?: string) => void
}

interface XeroFormActionsProps {
  isSubmitting: boolean
  onCancel?: (() => void) | undefined
}

function XeroFormActions({ isSubmitting, onCancel }: XeroFormActionsProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="submit"
        disabled={isSubmitting}
        className="flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('licensing.xero.saving')}
          </>
        ) : (
          <>
            <Save className="h-4 w-4" />
            {t('licensing.xero.saveInvoiceState')}
          </>
        )}
      </button>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
        >
          {t('actions.cancel', { ns: 'common' })}
        </button>
      )}
    </div>
  )
}

interface XeroFormBodyProps {
  formData: XeroFormDataShape
  amountsFormData: XeroFormDataShape & {
    amountDue: string
    amountPaid: string
    amountCredited: string
  }
  invoiceStatuses: { value: XeroInvoiceStatus; label: string }[]
  organisations: import('../../types').Organisation[]
  selectedOrgId: string
  isLoadingOrgs: boolean
  isSubmitting: boolean
  error: string | null
  result: ImportResponse | null
  onCancel?: (() => void) | undefined
  onCreateOrganisation?: ((xeroOrganisationId?: string) => void) | undefined
  onOrgChange: (orgId: string) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onSubmit: (e: React.FormEvent) => void
}

function XeroFormBody({
  formData,
  amountsFormData,
  invoiceStatuses,
  organisations,
  selectedOrgId,
  isLoadingOrgs,
  isSubmitting,
  error,
  result,
  onCancel,
  onCreateOrganisation,
  onOrgChange,
  onChange,
  onAmountChange,
  onSubmit,
}: XeroFormBodyProps): React.ReactElement {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <XeroOrgFieldset
        organisations={organisations}
        selectedOrgId={selectedOrgId}
        isLoadingOrgs={isLoadingOrgs}
        xeroOrganisationId={formData.xeroOrganisationId}
        onOrgChange={onOrgChange}
        onCreateOrganisation={onCreateOrganisation}
        onConfirmLoseData={() => true}
      />
      <XeroInvoiceReferenceFieldset
        formData={formData}
        selectedOrgXeroId={
          organisations.find((org) => org.id === selectedOrgId)?.xeroOrganisationId ?? undefined
        }
        onChange={onChange}
      />
      <XeroFormStatusAmounts
        formData={amountsFormData}
        invoiceStatuses={invoiceStatuses}
        onChange={onAmountChange}
      />
      <XeroFormDatesLinking formData={formData} onChange={onChange} />
      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
      {result && (
        <XeroResultAlert
          result={result}
          xeroOrganisationId={formData.xeroOrganisationId}
          onCreateOrganisation={onCreateOrganisation}
        />
      )}
      <XeroFormActions isSubmitting={isSubmitting} onCancel={onCancel} />
    </form>
  )
}

function buildInvoiceStatuses(
  t: ReturnType<typeof useTranslation<'admin'>>['t']
): { value: XeroInvoiceStatus; label: string }[] {
  return [
    { value: 'DRAFT', label: t('licensing.xero.statusDraft') },
    { value: 'SUBMITTED', label: t('licensing.xero.statusSubmitted') },
    { value: 'AUTHORISED', label: t('licensing.xero.statusAuthorised') },
    { value: 'PAID', label: t('licensing.xero.statusPaid') },
    { value: 'VOIDED', label: t('licensing.xero.statusVoided') },
    { value: 'DELETED', label: t('licensing.xero.statusDeleted') },
  ]
}

export function XeroManualForm({
  initialData,
  onSuccess,
  onError,
  onCancel,
  onCreateOrganisation,
}: XeroManualFormProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const {
    formData, setFormData,
    amountDueStr, setAmountDueStr,
    amountPaidStr, setAmountPaidStr,
    amountCreditedStr, setAmountCreditedStr,
    isSubmitting, result, error,
    organisations, selectedOrgId, setSelectedOrgId, isLoadingOrgs,
    submitForm,
  } = useXeroManualForm({ initialData, onSuccess, onError })

  const handleOrgChange = (orgId: string): void => {
    setSelectedOrgId(orgId)
    if (!orgId) return
    const org = organisations.find((item) => item.id === orgId)
    if (org?.xeroOrganisationId)
      setFormData((prev) => ({ ...prev, xeroOrganisationId: org.xeroOrganisationId ?? '' }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target
    if (name === 'amountDue') { setAmountDueStr(value); return }
    if (name === 'amountPaid') { setAmountPaidStr(value); return }
    if (name === 'amountCredited') { setAmountCreditedStr(value); return }
    handleChange(e)
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    void submitForm({ formData, selectedOrgId, amountDueStr, amountPaidStr, amountCreditedStr })
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-800">
      <h3 className="mb-4 text-lg font-semibold text-stone-900 dark:text-white">
        {t('licensing.xero.manualEntry')}
      </h3>
      <p className="mb-6 text-sm text-stone-600 dark:text-stone-400">
        {t('licensing.xero.manualEntryDescription')}
      </p>
      <XeroFormBody
        formData={formData}
        amountsFormData={{ ...formData, amountDue: amountDueStr, amountPaid: amountPaidStr, amountCredited: amountCreditedStr }}
        invoiceStatuses={buildInvoiceStatuses(t)}
        organisations={organisations}
        selectedOrgId={selectedOrgId}
        isLoadingOrgs={isLoadingOrgs}
        isSubmitting={isSubmitting}
        error={error}
        result={result}
        onCancel={onCancel}
        onCreateOrganisation={onCreateOrganisation}
        onOrgChange={handleOrgChange}
        onChange={handleChange}
        onAmountChange={handleAmountChange}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
